// Bankkonten (X2): Liste im Firmenstamm, ein Standardkonto, IBAN nach Anlage unveränderlich
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Landmark, Plus, Edit2, Star, Ban, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const EMPTY = {
  label: '',
  account_holder: 'CTI Verwaltungs GmbH',
  iban: '',
  bic: '',
  bank_name: '',
  notice_text: '',
  notice_until: '',
};

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

export const last4 = (account) => (account?.iban || '').slice(-4);

export default function BankAccountsPanel({ api, canManage }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = neues Konto
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(null);
  const [replacementId, setReplacementId] = useState('');
  const [affected, setAffected] = useState([]);

  const load = async () => {
    try {
      const res = await api.get('/bank-accounts');
      setAccounts(res.data || []);
    } catch (e) {
      toast.error('Bankkonten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setEditOpen(true);
  };

  const openEdit = (acc) => {
    setEditing(acc);
    setForm({
      label: acc.label || '',
      account_holder: acc.account_holder || '',
      iban: acc.iban_display || acc.iban || '',
      bic: acc.bic || '',
      bank_name: acc.bank_name || '',
      notice_text: acc.notice_text || '',
      notice_until: acc.notice_until || '',
    });
    setEditOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        delete payload.iban; // IBAN ist nach Anlage gesperrt
        await api.put(`/bank-accounts/${editing.id}`, payload);
        toast.success('Bankkonto gespeichert');
      } else {
        await api.post('/bank-accounts', payload);
        toast.success('Bankkonto angelegt');
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(errorText(e, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (acc) => {
    try {
      await api.post(`/bank-accounts/${acc.id}/set-default`);
      toast.success(`${acc.label} ist jetzt Standardkonto`);
      load();
    } catch (e) {
      toast.error(errorText(e, 'Fehler beim Festlegen'));
    }
  };

  const startDeactivate = (acc) => {
    setDeactivating(acc);
    setReplacementId('');
    setAffected([]);
  };

  const confirmDeactivate = async () => {
    try {
      const res = await api.post(`/bank-accounts/${deactivating.id}/deactivate`, {
        replacement_id: replacementId || null,
      });
      const n = res.data?.reassigned || 0;
      toast.success(n > 0 ? `Konto deaktiviert, ${n} Kunden umgestellt` : 'Konto deaktiviert');
      setDeactivating(null);
      load();
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.customers) {
        setAffected(e.response.data.detail.customers);
      } else {
        toast.error(errorText(e, 'Fehler beim Deaktivieren'));
      }
    }
  };

  const activeOthers = accounts.filter((a) => a.active && a.id !== deactivating?.id);

  return (
    <div className="card-glass p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-foreground">Bankkonten</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Auf welches Konto ein Kunde zahlt, wird beim Kunden gewählt (Reiter „Rechnung“). Ohne Auswahl gilt das Standardkonto.
            Beim Ausstellen wird das Konto fest in die Rechnung übernommen.
          </p>
        </div>
        {canManage && (
          <Button type="button" onClick={openNew} className="bg-indigo-500 hover:bg-indigo-600 text-white flex-shrink-0">
            <Plus className="w-4 h-4 mr-2" />
            Konto anlegen
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : accounts.length === 0 ? (
        <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground">
          Noch kein Bankkonto angelegt. Ohne Konto kann keine Rechnung ausgestellt werden.
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((acc) => (
            <div
              key={acc.id}
              className={`flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-lg border border-border ${acc.active ? 'bg-card' : 'bg-muted/40 opacity-70'}`}
            >
              <Landmark className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">{acc.label}</span>
                  {acc.is_default && <Badge className="bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 border-indigo-500/30">Standard</Badge>}
                  {!acc.active && <Badge variant="outline">deaktiviert</Badge>}
                </div>
                <p className="text-sm text-foreground font-mono mt-1">{acc.iban_display}</p>
                <p className="text-xs text-muted-foreground">
                  {[acc.bank_name, acc.bic && `BIC ${acc.bic}`, acc.account_holder].filter(Boolean).join(' · ')}
                </p>
                {acc.notice_text && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Hinweis auf der Rechnung: „{acc.notice_text}“{acc.notice_until ? ` (bis ${acc.notice_until})` : ''}
                  </p>
                )}
              </div>
              {canManage && acc.active && (
                <div className="flex gap-1 flex-shrink-0">
                  <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(acc)} aria-label="Bearbeiten">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {!acc.is_default && (
                    <>
                      <Button type="button" variant="ghost" size="sm" onClick={() => makeDefault(acc)}>
                        <Star className="w-4 h-4 mr-1" />
                        Standard
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => startDeactivate(acc)} className="hover:text-red-500">
                        <Ban className="w-4 h-4 mr-1" />
                        Deaktivieren
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Anlegen / Bearbeiten */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">{editing ? 'Bankkonto bearbeiten' : 'Bankkonto anlegen'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Die IBAN ist gesperrt. Für eine neue Bankverbindung ein neues Konto anlegen und dieses deaktivieren.'
                : 'Die IBAN kann nach dem Anlegen nicht mehr geändert werden.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Bezeichnung (intern) *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="z. B. Sparkasse Stade – Projekte Nord" className="bg-input border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Kontoinhaber * (BT-85)</Label>
              <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className="bg-input border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground flex items-center gap-1">
                IBAN * (BT-84){editing && <Lock className="w-3.5 h-3.5" />}
              </Label>
              <Input
                value={form.iban}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                placeholder="DE.. .... .... .... .... .."
                className="bg-input border-border font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-muted-foreground">BIC (BT-86)</Label>
                <Input value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} className="bg-input border-border font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Bank</Label>
                <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="bg-input border-border" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Hinweis auf der Rechnung (optional)</Label>
              <Input value={form.notice_text} onChange={(e) => setForm({ ...form, notice_text: e.target.value })} placeholder="z. B. Bitte beachten Sie unsere neue Bankverbindung." className="bg-input border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Hinweis anzeigen bis (optional)</Label>
              <Input type="date" value={form.notice_until} onChange={(e) => setForm({ ...form, notice_until: e.target.value })} className="bg-input border-border max-w-[200px]" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button type="button" onClick={save} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-white">
              {editing ? 'Speichern' : 'Anlegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deaktivieren */}
      <Dialog open={!!deactivating} onOpenChange={(open) => !open && setDeactivating(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">Bankkonto deaktivieren</DialogTitle>
            <DialogDescription>
              {deactivating?.label} ({deactivating?.iban_display}) erscheint danach auf keiner neuen Rechnung mehr. Ausgestellte Rechnungen bleiben unverändert.
            </DialogDescription>
          </DialogHeader>
          {affected.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-foreground">
                Das Konto ist {affected.length} Kunden zugeordnet: {affected.slice(0, 6).join(', ')}{affected.length > 6 ? ' …' : ''}. Bitte ein Ersatzkonto wählen:
              </p>
              <Select value={replacementId || undefined} onValueChange={setReplacementId}>
                <SelectTrigger className="bg-input border-border">
                  <SelectValue placeholder="Ersatzkonto wählen" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {activeOthers.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.label} ··{last4(a)}{a.is_default ? ' (Standard)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setDeactivating(null)}>Abbrechen</Button>
            <Button
              type="button"
              onClick={confirmDeactivate}
              disabled={affected.length > 0 && !replacementId}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Deaktivieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
